import { LupaIcon } from "assets/icons/LupaIcon";
import { showErrorToast } from "components/CustomAlerts/CustomToast";
import { Input } from "components/Input/Input";
import { Pagination } from "components/Pagination/Pagination";
import { Select } from "components/Select/Select";
import { DEFAULT_LIMIT_PAGINATION } from "constants/appConstants";
import { useFormik } from "formik";
import { EspacioCurricularI } from "interfaces/EspacioCurricular";
import { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PRIVATE_ROUTE } from "routes/privateRoutes";
import { getEspaciosCurriculares } from "services/espacioCurricular";
import { fetchEstados } from "store/slices/appSlice";
import { AppDispatch, RootState } from "store/store";
import { EspaciosCurricularesRowActions } from "./EspaciosCurricularesTable/EspaciosCurricularesRowActions";
import { EspaciosCurricularesTable } from "./EspaciosCurricularesTable/EspaciosCurricularesTable";

export const EspaciosCurriculares = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [elementos, setElementos] = useState<EspacioCurricularI[]>();
  const [cantidad, setCantidad] = useState<number>();
  const [page, setPage] = useState<number>();
  const [limit, setLimit] = useState<number>();

  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { estados, loadingAppData } = useSelector(
    (st: RootState) => st.appData,
  );

  const initialValues = {
    nombre: "",
  };

  const { values, handleChange, resetForm } = useFormik({
    initialValues,
    onSubmit: () => {},
  });

  const searchEspacios = async (params?: {
    filtros?: { [key: string]: any };
  }) => {
    try {
      setIsLoading(true);
      const response = await getEspaciosCurriculares({ ...params?.filtros });
      setElementos(response.elementos);
      setCantidad(response.cantidad);
      setPage(response.page);
      setLimit(params?.filtros?.limit ?? DEFAULT_LIMIT_PAGINATION);
    } catch (err) {
      showErrorToast("No fue posible cargar los espacios curriculares");
    } finally {
      setIsLoading(false);
    }
  };

  const refreshTable = () => {
    setPage(1);
    searchEspacios({ filtros: { ...values, page: 1 } });
  };

  const handleEdit = (elemento: EspacioCurricularI) => {
    navigate(PRIVATE_ROUTE.ESPACIOS_CURRICULARES_EDITAR + "/" + elemento.id);
  };

  const handleChangePagination = (newFiltros: {
    [key: string]: string | number;
  }) => {
    searchEspacios({ filtros: { ...values, ...newFiltros } });
  };

  const handleClearFilters = () => {
    resetForm();
    searchEspacios();
  };

  const handleCreate = () => {
    navigate(PRIVATE_ROUTE.ESPACIOS_CURRICULARES_CREAR);
  };

  const handleChangeWithResetPage = (pageOne: any) => {
    setPage(1);
    handleChange(pageOne);
  };

  const renderActions = (
    elemento: EspacioCurricularI,
    onClickShowDetail: (elemento: EspacioCurricularI) => void,
  ) => {
    return (
      <EspaciosCurricularesRowActions
        elemento={elemento}
        onClickEdit={handleEdit}
        refreshTable={refreshTable}
        onClickShowDetail={onClickShowDetail}
      />
    );
  };

  useEffect(() => {
    searchEspacios({ filtros: { ...values, limit, page } });
  }, [values]);

  useEffect(() => {
    if (!estados) dispatch(fetchEstados());
  }, []);

  return (
    <section>
      <h1 className="section-title">Espacios curriculares</h1>

      <h2 className="section-subtitle mt-2">
        Desde acá podrás gestionar los espacios curriculares.
      </h2>

      <div className="d-flex justify-content-between mt-4 mb-3">
        <div className="d-flex column-gap-3">
          <Input
            size="sm"
            id="nombre"
            onChange={handleChangeWithResetPage}
            placeholder="Buscar"
            values={values}
            startIcon={<LupaIcon />}
          />
          <Button
            size="sm"
            variant="secondary"
            className="clear-filters-button"
            onClick={handleClearFilters}
            disabled={!values.nombre}
          >
            Limpiar filtros
          </Button>
        </div>
        <Button size="lg" onClick={handleCreate}>
          Nuevo espacio curricular
        </Button>
      </div>

      {isLoading && (
        <div className="d-flex justify-content-center w-full">
          <Spinner />
        </div>
      )}

      {!isLoading && elementos && cantidad != undefined && (
        <>
          <EspaciosCurricularesTable
            elementos={elementos}
            renderActions={renderActions}
          />
          <Pagination
            totalItems={cantidad}
            currentPage={page}
            currentLimit={limit}
            onChangePage={handleChangePagination}
            showPaginationOnlyOnMoreThanPages={1}
          />
        </>
      )}
    </section>
  );
};
